// client/src/dnd/emitter_tool/engine/engine_parts/sprites/MultiSource.js
import * as PIXI from "pixi.js";

/**
 * MultiSource
 * Emits from a fixed list with strict equal distribution (round-robin).
 * Name resolution is robust (exact → basename → substring), and happens
 * lazily so late-arriving atlases work without rebuilds.
 */
export default class MultiSource {
  /**
   * @param {Object} opts
   * @param {Record<string, PIXI.Texture>} opts.textures
   * @param {string[]} opts.list  // pool of texture names (UI-selected)
   */
  constructor({ textures = {}, list = [] } = {}) {
    this.textures = textures || {};
    // keep the raw requested names; we resolve to atlas keys on demand
    this.requested = Array.isArray(list) ? list.slice() : [];
    this._lastResolvedKeys = [];
    this._idx = 0; // round-robin pointer
  }

  // ---- helpers (match SingleSource’s resolver) ----
  _keys() {
    try { return Object.keys(this.textures || {}); } catch { return []; }
  }
  _norm(s) { return String(s || "").trim().toLowerCase(); }
  _baseNoExt(str) {
    const slash = Math.max(str.lastIndexOf("/"), str.lastIndexOf("\\"));
    const base = slash >= 0 ? str.slice(slash + 1) : str;
    const dot  = base.lastIndexOf(".");
    return dot > 0 ? base.slice(0, dot) : base;
  }
  _resolveOne(req) {
    const keys = this._keys();
    if (!keys.length) return null;
    if (!req) return null;
    const low = this._norm(req);
    // exact
    let k = keys.find((n) => this._norm(n) === low);
    if (k) return k;
    // basename
    k = keys.find((n) => this._baseNoExt(n).toLowerCase() === low);
    if (k) return k;
    // substring
    k = keys.find((n) => this._norm(n).includes(low));
    return k || null;
  }
  _resolvePool() {
    const seen = new Set();
    const out = [];

    // resolve requested names into atlas keys
    for (const req of (this.requested || [])) {
      const key = this._resolveOne(req);
      if (key && !seen.has(key) && this.textures[key]) {
        seen.add(key);
        out.push(key);
      }
    }

    // if nothing chosen/valid, fallback to all keys (sorted stable for determinism)
    if (!out.length) {
      const all = this._keys().filter((k) => !!this.textures[k]);
      all.sort((a, b) => a.localeCompare(b));
      return all;
    }

    return out;
  }

  /** @param {number} _dt seconds (ignored) */
  next(_dt = 0) {
    const resolved = this._resolvePool();

    // keep round-robin index stable across same pool; reset if pool changed/empty
    const poolChanged =
      resolved.length !== this._lastResolvedKeys.length ||
      resolved.some((k, i) => k !== this._lastResolvedKeys[i]);

    if (poolChanged) {
      this._lastResolvedKeys = resolved.slice();
      if (this._idx >= resolved.length) this._idx = 0;
    }

    if (!resolved.length) return PIXI.Texture.WHITE;

    const key = resolved[this._idx];
    const tex = this.textures[key];

    // advance pointer for next call (strict equal-share)
    this._idx = (this._idx + 1) % resolved.length;

    // never return WHITE if at least one texture exists
    if (tex) return tex;
    const first = this.textures[resolved[0]];
    return first || PIXI.Texture.WHITE;
  }
}
